import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import UserAvatar from "../components/UserAvatar";
import { Loader2, ArrowLeft, Trophy, Flame, Star, FileText, Heart, Users, Home, Search, Plus, Bell, User } from "lucide-react";
import api from "../lib/api";

const PERIODS = [
  { id: "weekly", label: "Weekly" },
  { id: "monthly", label: "Monthly" },
  { id: "all_time", label: "All Time" },
];

const CATEGORIES = [
  { id: "streaks", label: "Streaks", icon: Flame, emoji: "🔥" },
  { id: "achievements", label: "Achievements", icon: Trophy, emoji: "🏆" },
  { id: "posts", label: "Posts", icon: FileText, emoji: "📝" },
  { id: "likes_received", label: "Most Loved", icon: Heart, emoji: "❤️" },
  { id: "followers", label: "Most Followed", icon: Users, emoji: "👥" },
];

const MEDALS = ["🥇", "🥈", "🥉"];

export default function LeaderboardPage() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("all_time");
  const [category, setCategory] = useState("posts");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    api.get("/profile/me").then((r) => setProfile(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get(`/leaderboard?period=${period}&category=${category}`)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [period, category]);

  const catConfig = CATEGORIES.find((c) => c.id === category) || CATEGORIES[0];

  return (
    <div className="min-h-screen pb-20 lg:pb-0" data-testid="leaderboard-page">
      <div className="sticky top-0 z-30 nav-glass px-4 h-12 flex items-center gap-3">
        <button onClick={() => navigate("/feed")} className="text-muted-foreground hover:text-foreground/80 bg-transparent border-0 cursor-pointer"><ArrowLeft className="w-5 h-5" /></button>
        <Trophy className="w-4 h-4 text-amber-500" />
        <h1 className="font-outfit text-base font-semibold text-foreground">Leaderboard</h1>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-5 space-y-4">
        {/* Period Toggle */}
        <div className="flex gap-1.5 bg-card border border-border/60 rounded-xl p-1" data-testid="period-toggle">
          {PERIODS.map((p) => (
            <button key={p.id} onClick={() => setPeriod(p.id)} className={`flex-1 py-2 rounded-lg text-xs font-medium border-0 cursor-pointer transition-all ${period === p.id ? "bg-indigo-500 text-white shadow-sm" : "bg-transparent text-muted-foreground hover:bg-white/5"}`} data-testid={`period-${p.id}`}>
              {p.label}
            </button>
          ))}
        </div>

        {/* Category Tabs */}
        <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="category-tabs">
          {CATEGORIES.map((c) => (
            <button key={c.id} onClick={() => setCategory(c.id)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border-0 cursor-pointer transition-all ${category === c.id ? "bg-cyan-500/10 text-cyan-400 ring-1 ring-cyan-400/30" : "bg-card text-muted-foreground hover:bg-white/5 border border-border/60"}`} data-testid={`cat-${c.id}`}>
              <span>{c.emoji}</span> {c.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>
        ) : !data || data.entries.length === 0 ? (
          <div className="text-center py-16 bg-card rounded-xl border border-border/60">
            <Trophy className="w-8 h-8 text-muted-foreground/60 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No rankings yet for this category</p>
          </div>
        ) : (
          <>
            {/* Top 3 Podium */}
            <div className="grid grid-cols-3 gap-3 mb-2" data-testid="top-3">
              {[1, 0, 2].map((idx) => {
                const entry = data.entries[idx];
                if (!entry) return <div key={idx} />;
                const isFirst = idx === 0;
                return (
                  <Link to={`/profile/${entry.username}`} key={entry.user_id} className={`border rounded-xl p-4 text-center no-underline transition-all hover:-translate-y-1 ${isFirst ? "bg-gradient-to-b from-amber-500/10 to-yellow-500/10 border-amber-500/40 shadow-[0_0_20px_-3px_rgba(245,158,11,0.3)] order-first col-start-2" : idx === 1 ? "bg-gradient-to-b from-gray-50 to-slate-50 border-border" : "bg-gradient-to-b from-orange-500/10 to-amber-500/10 border-orange-500/30"}`} data-testid={`top-${idx + 1}`}>
                    <div className="text-2xl mb-1">{MEDALS[idx] || ""}</div>
                    <UserAvatar name={entry.display_name} username={entry.username} avatarUrl={entry.avatar_url} size={isFirst ? "lg" : "md"} className="mx-auto mb-2" />
                    <p className="text-xs font-semibold text-foreground truncate">{entry.display_name}</p>
                    <p className="text-[10px] text-muted-foreground/70">@{entry.username}</p>
                    <p className="text-base font-bold text-cyan-400 mt-1 font-outfit">{entry.score}</p>
                  </Link>
                );
              })}
            </div>

            {/* Rest of Rankings */}
            <div className="bg-card border border-border/60 rounded-xl overflow-hidden" data-testid="rankings-list">
              {data.entries.slice(3).map((entry) => (
                <Link to={`/profile/${entry.username}`} key={entry.user_id} className={`flex items-center gap-3 px-4 py-3 border-b border-border/40 last:border-0 no-underline hover:bg-white/5 transition-colors ${entry.user_id === data.entries[0]?.user_id ? "" : ""}`} data-testid={`rank-${entry.rank}`}>
                  <span className="w-6 text-center text-xs font-semibold text-muted-foreground/70">{entry.rank}</span>
                  <UserAvatar name={entry.display_name} username={entry.username} avatarUrl={entry.avatar_url} size="sm" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-foreground truncate">{entry.display_name}</p>
                    <p className="text-[10px] text-muted-foreground/70">@{entry.username}</p>
                  </div>
                  <span className="text-sm font-semibold text-cyan-400 font-outfit">{entry.score}</span>
                </Link>
              ))}
            </div>

            {/* My Rank */}
            {data.my_rank && (
              <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-xl px-4 py-3 flex items-center justify-between" data-testid="my-rank">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-cyan-400">Your Rank</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-bold text-cyan-300">#{data.my_rank}</span>
                  <span className="text-xs text-cyan-400">{data.my_score} {catConfig.label.toLowerCase()}</span>
                </div>
              </div>
            )}
            {!data.my_rank && (
              <div className="bg-muted/40 border border-border/60 rounded-xl px-4 py-3 text-center" data-testid="my-rank-none">
                <p className="text-xs text-muted-foreground">You're not ranked yet. Start posting to join the leaderboard!</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Mobile Bottom Tabs */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border/60 h-14 flex items-center justify-around px-2">
        <Link to="/feed" className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><Home className="w-5 h-5" /><span className="text-[10px]">Home</span></Link>
        <Link to="/explore" className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><Search className="w-5 h-5" /><span className="text-[10px]">Explore</span></Link>
        <Link to="/feed" className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><Plus className="w-5 h-5" /></Link>
        <Link to="/notifications" className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><Bell className="w-5 h-5" /><span className="text-[10px]">Notifs</span></Link>
        <Link to={profile ? `/profile/${profile.username}` : "/feed"} className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><User className="w-5 h-5" /><span className="text-[10px]">Profile</span></Link>
      </div>
    </div>
  );
}
